import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { ADVERTISEMENT, CONTENT_TYPE } from "../config/constants";

const AD_KEY = ["advertisements"];

const invalidateAds = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: AD_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["advertisement", id] });
};

export const useGetAllAdvertisements = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...AD_KEY, params],
    queryFn: async ({ signal }) => {
      const { data } = await Get(ADVERTISEMENT.getAllAdvertisements, token, params, {
        signal,
      });
      return data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetAdvertisementById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["advertisement", id],
    queryFn: async () => {
      const { data } = await Get(ADVERTISEMENT.getAdvertisementById + id, token);
      return data?.advertisement;
    },
    enabled: !!token && !!id,
  });
};

export const useAddAdvertisement = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ data }) => {
      const response = await Post(
        ADVERTISEMENT.addAdvertisement,
        data,
        token,
        null,
        CONTENT_TYPE.FORM_DATA
      );
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create advertisement");
    },
    onSuccess: () => invalidateAds(queryClient),
  });
};

export const useUpdateAdvertisement = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await Post(
        ADVERTISEMENT.updateAdvertisement + id,
        data,
        token,
        null,
        CONTENT_TYPE.FORM_DATA
      );
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update advertisement");
    },
    onSuccess: (_data, variables) => invalidateAds(queryClient, variables?.id),
  });
};

export const useDeleteAdvertisement = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(ADVERTISEMENT.deleteAdvertisement + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete advertisement");
    },
    onSuccess: () => invalidateAds(queryClient),
  });
};
